import { compare, getData, IDataAccessService } from '@price-for-profit/micro-services';
import { CLIENT_ID, DATABASE_LABEL } from 'shared/constants';
import {
  AcceptParameters,
  AcceptPriceNamedQueryParameters,
  AssignedCommoditySqlView,
  CommoditySqlView,
  CommoditySummarySqlView,
  CustomerDeliveredPrice,
  DatabaseLabel,
  IdParameters,
  ProductSql,
  ProductsSummaryMetrics,
  ProductUpdate,
  RevisedBestPriceParameters,
  RevisedPriceNamedQueryParameters,
  UserInfo,
} from 'shared/types';
import { commoditySummaryToProductMetrics } from 'shared/utility';

const COMMODITY_VIEW = 'vw_commodity';
const COMMODITY_SUMMARY_VIEW = 'vw_commodity_summary';
const ASSIGNED_COMMODITY_VIEW = 'vw_assigned_commodity';
const CUSTOMER_DELIVERED_PRICE_VIEW = 'vw_customer_delivered_price';
const PRODUCT_TABLE_NAME = 'Product';

export interface UserMappingProps {
  assignedCommodities: string[];
  user: UserInfo;
}

export interface PatchProductProps {
  oldRow: CommoditySqlView;
  newRow: CommoditySqlView;
  user: UserInfo;
}
export interface MassActionRevisedPriceProps {
  ids: IdParameters[];
  parameters: RevisedBestPriceParameters;
}

export interface AcceptProductProps {
  ids: IdParameters[];
  parameters: AcceptParameters;
}
export interface IProductService {
  getGridData(userId?: string): Promise<CommoditySqlView[]>;
  getGridSummary(): Promise<ProductsSummaryMetrics>;
  patchProduct(props: PatchProductProps): Promise<CommoditySqlView>;
  massActionRevisedPrice(props: MassActionRevisedPriceProps): Promise<void>;
  acceptProducts(props: AcceptProductProps): Promise<CommoditySqlView[]>;
  getCustomerDeliveredPrices(productNumber: string): Promise<CustomerDeliveredPrice[]>;
}

type EditableFields = Pick<
  ProductUpdate,
  | 'revisedCost'
  | 'revisedBestPrice'
  | 'revisedSpotBestPrice'
  | 'status'
  | 'updatedBy'
  | 'updatedByEmail'
  | 'updatedDate'
>;

function commodityViewToUpdateProduct(view: CommoditySqlView): EditableFields {
  return {
    revisedCost: view.revisedCost,
    revisedBestPrice: view.revisedBestPrice,
    revisedSpotBestPrice: view.revisedSpotBestPrice,
    status: 'Needs Review',
    updatedDate: '',
    updatedBy: '',
    updatedByEmail: '',
  };
}

function updateMetadataFields(editedProduct: EditableFields, user: UserInfo): ProductUpdate {
  const today = new Date().toISOString();
  return {
    ...editedProduct,
    status: 'Modified',
    updatedDate: today,
    updatedBy: user.id,
    updatedByEmail: user.email,
  };
}

function productSqlToCommoditySqlView(productSql: ProductSql, existing: CommoditySqlView): CommoditySqlView {
  return {
    ...existing,
    revisedCost: productSql.revisedCost,
    revisedBestPrice: productSql.revisedBestPrice,
    revisedSpotBestPrice: productSql.revisedSpotBestPrice,
    status: productSql.status,
    updatedDate: productSql.updatedDate,
  };
}

export class ProductService implements IProductService {
  constructor(private dataAccessService: IDataAccessService) {}

  async getCustomerDeliveredPrices(productNumber: string): Promise<CustomerDeliveredPrice[]> {
    const { data } = await this.dataAccessService.getCollection<CustomerDeliveredPrice, DatabaseLabel>({
      clientId: CLIENT_ID,
      databaseLabel: 'main',
      tableId: CUSTOMER_DELIVERED_PRICE_VIEW,
      pageSize: 1000,
      sortBy: 'masterCustomerCode',
      collectionFilter: {
        property: 'productNumber',
        operator: 'eq',
        value: productNumber,
      },
    });

    return data;
  }

  async acceptProducts({ ids, parameters }: AcceptProductProps): Promise<CommoditySqlView[]> {
    const { data } = await this.dataAccessService.executeNamedQuery<
      CommoditySqlView,
      AcceptPriceNamedQueryParameters,
      DatabaseLabel
    >({
      clientId: CLIENT_ID,
      databaseLabel: 'main',
      queryName: 'sp_accept_products',
      parameters: { IdParameters: ids, AcceptParameters: [parameters] }, // FIX
      partitionKey: '',
    });

    return data;
  }

  async massActionRevisedPrice({ ids, parameters }: MassActionRevisedPriceProps): Promise<void> {
    await this.dataAccessService.executeNamedQuery<void, RevisedPriceNamedQueryParameters, DatabaseLabel>({
      clientId: CLIENT_ID,
      databaseLabel: 'main',
      queryName: 'sp_product_mass_action',
      parameters: { IdParameters: ids, RevisedBestPriceParameters: [parameters] }, // FIX
      partitionKey: '',
    });
  }

  async patchProduct({ newRow, oldRow, user }: PatchProductProps): Promise<CommoditySqlView> {
    const [newProduct, oldProduct] = [newRow, oldRow].map(commodityViewToUpdateProduct);

    const operations = compare(oldProduct, updateMetadataFields(newProduct, user));

    const { data } = await this.dataAccessService.patchRow<ProductSql, DatabaseLabel>({
      clientId: CLIENT_ID,
      databaseLabel: DATABASE_LABEL,
      tableId: PRODUCT_TABLE_NAME,
      key: newRow.id,
      operations,
    });

    return productSqlToCommoditySqlView(data, newRow);
  }

  async getGridSummary(): Promise<ProductsSummaryMetrics> {
    const { data } = await this.dataAccessService.getCollection<CommoditySummarySqlView, DatabaseLabel>({
      clientId: CLIENT_ID,
      databaseLabel: DATABASE_LABEL,
      tableId: COMMODITY_SUMMARY_VIEW,
      enableTotalCount: false,
    });

    return commoditySummaryToProductMetrics(data[0]);
  }

  async getGridData(userId?: string): Promise<CommoditySqlView[]> {
    if (userId) {
      return await getData(page =>
        this.dataAccessService.getCollection<AssignedCommoditySqlView, DatabaseLabel>({
          clientId: CLIENT_ID,
          databaseLabel: DATABASE_LABEL,
          tableId: ASSIGNED_COMMODITY_VIEW,
          collectionFilter: {
            property: 'userId',
            operator: 'eq',
            value: userId,
          },
          pageSize: 1000,
          page,
        })
      );
    }
    return await getData(page =>
      this.dataAccessService.getCollection<CommoditySqlView, DatabaseLabel>({
        clientId: CLIENT_ID,
        databaseLabel: DATABASE_LABEL,
        tableId: COMMODITY_VIEW,
        pageSize: 1000,
        page,
      })
    );
  }
}
